/**
 * Login Page — PIN-based auth for shared tablets
 *
 * 1. Select your name from the staff list
 * 2. Enter 4-digit PIN
 * 3. You're in
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore, type Staff } from '../stores/auth-store';

export function LoginPage() {
  const { login, isLoading, authError, clearError } = useAuthStore();
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [pin, setPin] = useState('');
  const [loadingStaff, setLoadingStaff] = useState(true);

  useEffect(() => {
    useAuthStore.getState().fetchStaffList().then(list => {
      setStaffList(list);
      setLoadingStaff(false);
    });
  }, []);

  const handlePinDigit = useCallback((digit: string) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);

    // Auto-submit on 4 digits
    if (newPin.length === 4 && selectedStaff) {
      clearError();
      login(selectedStaff.id, newPin).then(success => {
        if (!success) setPin('');
      });
    }
  }, [pin, selectedStaff, login, clearError]);

  const handleBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
    clearError();
  }, [clearError]);

  const handleBack = useCallback(() => {
    setSelectedStaff(null);
    setPin('');
    clearError();
  }, [clearError]);

  // Team colors
  const teamColor = (team: string | null) => {
    switch (team) {
      case 'wasserij': return 'bg-blue-100 text-blue-700';
      case 'route': return 'bg-emerald-100 text-emerald-700';
      case 'kantoor': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Staff selection screen
  if (!selectedStaff) {
    return (
      <div className="w-full max-w-md">
        <div className="kleda-card text-center">
          <div className="mb-lg flex justify-center">
            <div className="w-20 h-20 bg-kleda-primary rounded-kleda-card flex items-center justify-center shadow-kleda-md">
              <span className="text-white font-heading text-2xl font-bold">KF</span>
            </div>
          </div>
          <h2 className="font-heading text-xl font-bold text-kleda-text-main mb-sm">
            Kleda Field
          </h2>
          <p className="text-kleda-text-muted text-sm mb-lg">
            Wie ben je?
          </p>

          {loadingStaff ? (
            <p className="text-kleda-text-muted py-8">Laden...</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {staffList.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedStaff(s)}
                  className="p-3 rounded-lg border border-gray-200 hover:border-kleda-primary hover:bg-kleda-primary/5 transition-colors text-left"
                >
                  <div className="font-medium text-kleda-text-main text-sm">{s.name}</div>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${teamColor(s.team)}`}>
                    {s.team || 'algemeen'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // PIN entry screen
  return (
    <div className="w-full max-w-sm">
      <div className="kleda-card text-center">
        <button
          onClick={handleBack}
          className="absolute top-4 left-4 text-kleda-text-muted hover:text-kleda-text-main"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="mb-md">
          <div className="w-14 h-14 mx-auto bg-kleda-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
            {selectedStaff.name.charAt(0)}
          </div>
          <h2 className="font-heading text-lg font-bold text-kleda-text-main mt-2">
            {selectedStaff.name}
          </h2>
          <p className="text-kleda-text-muted text-sm">Voer je PIN in</p>
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-3 mb-md">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-colors ${
                i < pin.length ? 'bg-kleda-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {authError && (
          <p className="text-red-600 text-sm mb-md">{authError}</p>
        )}

        {/* Loading */}
        {isLoading && (
          <p className="text-kleda-text-muted text-sm mb-md">Inloggen...</p>
        )}

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto">
          {['1','2','3','4','5','6','7','8','9'].map(d => (
            <button
              key={d}
              onClick={() => handlePinDigit(d)}
              disabled={isLoading}
              className="h-14 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 font-heading text-xl font-semibold text-kleda-text-main transition-colors disabled:opacity-50"
            >
              {d}
            </button>
          ))}
          <div />
          <button
            onClick={() => handlePinDigit('0')}
            disabled={isLoading}
            className="h-14 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 font-heading text-xl font-semibold text-kleda-text-main transition-colors disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={handleBackspace}
            disabled={isLoading}
            className="h-14 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414-6.414a2 2 0 011.414-.586H19a2 2 0 012 2v10a2 2 0 01-2 2h-8.172a2 2 0 01-1.414-.586L3 12z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
